import cn from "classnames";

import type { ContainerBuilderBlock } from "@/components/containerBuilder/containerBuilder.component";
import { RichText } from "@/components/richText/richText.component";

/**
 * Page builder block for a standalone run of rich text.
 *
 * `alignment` sets the default for the block; the per-line align marks authors
 * can apply inside the editor still win over it, since those render their own
 * alignment wrappers.
 */
export function TextBuilder({ block }: TextBuilderProps) {
  if (!block?.content?.length) return null;
  const direction = block.direction ?? "column";
  const alignment = block.alignment ?? "left";
  return (
    <section
      data-testid="text-builder"
      className={cn("flex w-full", {
        "flex-col": direction === "column",
        "flex-row": direction === "row",
        "justify-start": alignment === "left",
        "justify-center": alignment === "center",
        "justify-end": alignment === "right",
      })}>
      <RichText asH1={block.asH1}>{block.content}</RichText>
    </section>
  );
}

/** Width classes for the Container flex-item wrapper around a text block. */
export function textWidthStyles(
  width: TextBuilderWidth | null | undefined,
  alignment: TextBuilderAlignment | null | undefined = "left",
) {
  const align = alignment ?? "left";
  const isFractional =
    width === "half" ||
    width === "oneThird" ||
    width === "twoThirds" ||
    width === "quarter";

  return cn({
    "w-full": !width || width === "full",
    "w-full md:w-1/2": width === "half",
    "w-full md:w-1/3": width === "oneThird",
    "w-full md:w-2/3": width === "twoThirds",
    "w-full md:w-1/4": width === "quarter",
    "w-full max-w-3xl": width === "narrow",
    "md:mr-auto": isFractional && align === "left",
    "md:mx-auto": isFractional && align === "center",
    "md:ml-auto": isFractional && align === "right",
    "mr-auto": width === "narrow" && align === "left",
    "mx-auto": width === "narrow" && align === "center",
    "ml-auto": width === "narrow" && align === "right",
  });
}

export type TextBuilderWidth =
  | "full"
  | "half"
  | "oneThird"
  | "twoThirds"
  | "quarter"
  | "narrow";

type TextBuilderAlignment = "left" | "center" | "right";

type ContainerChild = NonNullable<ContainerBuilderBlock["children"]>[number];

export type TextBuilderBlock = Extract<
  ContainerChild,
  { _type: "textBuilder" }
>;

type TextBuilderProps = {
  block: TextBuilderBlock;
};
